import React from 'react';

/* ===============================
   TYPES
   =============================== */

// `auto`: sm por debajo de 1536 px y md desde 1536 px. Pensado para barras de herramientas.
type SegmentedControlSize = 'sm' | 'md' | 'lg' | 'auto';
type SegmentedControlMode = 'single' | 'multiple';

export interface SegmentedControlItem {
  value: string;
  label?: string;
  icon?: React.ReactNode;
  disabled?: boolean;
  ariaLabel?: string;
}

export interface SegmentedControlProps {
  items: SegmentedControlItem[];
  value: string | string[];
  onChange: (value: string | string[]) => void;
  selectionMode?: SegmentedControlMode;
  size?: SegmentedControlSize;
  disabled?: boolean;
  fullWidth?: boolean;
  className?: string;
}

/* ===============================
   SIZE TOKENS
   Track height matches Button scale: h-8 / h-10 / h-12
   Pill height = track - (3px padding × 2)
   =============================== */

const trackSize: Record<SegmentedControlSize, string> = {
  sm: 'h-8',
  md: 'h-10',
  lg: 'h-12',
  auto: 'h-8 2xl:h-10',
};

const pillHeight: Record<SegmentedControlSize, string> = {
  sm: 'h-[26px]',
  md: 'h-[34px]',
  lg: 'h-[42px]',
  auto: 'h-[26px] 2xl:h-[34px]',
};

const iconWidth: Record<SegmentedControlSize, string> = {
  sm: 'w-[26px]',
  md: 'w-[34px]',
  lg: 'w-[42px]',
  auto: 'w-[26px] 2xl:w-[34px]',
};

const iconSize: Record<SegmentedControlSize, string> = {
  sm: 'text-[15px]',
  md: 'text-[17px]',
  lg: 'text-[19px]',
  auto: 'text-[15px] 2xl:text-[17px]',
};

const txtPadding: Record<SegmentedControlSize, string> = {
  sm: 'px-[10px] text-[12px]',
  md: 'px-[13px] text-[13px]',
  lg: 'px-[16px] text-[14px]',
  auto: 'px-[10px] text-[12px] 2xl:px-[13px] 2xl:text-[13px]',
};

const mixPadding: Record<SegmentedControlSize, string> = {
  sm: 'px-[9px]  text-[12px] gap-[4px]',
  md: 'px-[11px] text-[13px] gap-[5px]',
  lg: 'px-[13px] text-[14px] gap-[6px]',
  auto: 'px-[9px] text-[12px] gap-[4px] 2xl:px-[11px] 2xl:text-[13px] 2xl:gap-[5px]',
};

/* ===============================
   SEGMENTED CONTROL
   =============================== */

export const SegmentedControl = React.forwardRef<HTMLDivElement, SegmentedControlProps>(
  (
    {
      items,
      value,
      onChange,
      selectionMode = 'single',
      size = 'md',
      disabled = false,
      fullWidth = false,
      className = '',
    },
    ref
  ) => {
    const selectedValues = Array.isArray(value) ? value : [value];
    const isSelected = (v: string) => selectedValues.includes(v);

    const handleClick = (itemValue: string) => {
      if (selectionMode === 'single') {
        if (isSelected(itemValue)) return;
        onChange(itemValue);
      } else {
        const next = isSelected(itemValue)
          ? selectedValues.filter((v) => v !== itemValue)
          : [...selectedValues, itemValue];
        onChange(next);
      }
    };

    const getItemForm = (item: SegmentedControlItem): 'ico' | 'txt' | 'mix' => {
      if (item.icon && !item.label) return 'ico';
      if (!item.icon && item.label) return 'txt';
      return 'mix';
    };

    const trackClasses = [
      'inline-flex items-center',
      'p-[3px] gap-[2px]',
      'rounded-lg',
      'bg-surface-2 border border-strong',
      trackSize[size],
      fullWidth ? 'w-full' : '',
      disabled ? 'opacity-50 pointer-events-none' : '',
      className,
    ]
      .filter(Boolean)
      .join(' ')
      .replace(/\s+/g, ' ');

    const getItemClasses = (item: SegmentedControlItem): string => {
      const active = isSelected(item.value);
      const isDisabled = item.disabled;
      const form = getItemForm(item);

      const base = [
        'inline-flex items-center justify-center',
        'border rounded-lg',
        "font-['Inter'] font-medium",
        'leading-none whitespace-nowrap flex-shrink-0',
        'transition-all duration-150',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-inset',
        'cursor-pointer',
        'p-0 m-0',
        '-webkit-appearance-none',
        pillHeight[size],
        fullWidth && form !== 'ico' ? 'flex-1' : '',
      ];

      if (form === 'ico') {
        base.push(iconWidth[size], iconSize[size]);
      } else if (form === 'txt') {
        base.push(txtPadding[size]);
      } else {
        base.push(mixPadding[size]);
      }

      if (active) {
        base.push(
          'bg-surface-0 text-secondary border-strong'
        );
      } else {
        base.push(
          'bg-transparent text-subtle border-transparent',
          'hover:text-secondary'
        );
      }

      if (isDisabled) base.push('opacity-40 cursor-not-allowed pointer-events-none');

      return base.filter(Boolean).join(' ').replace(/\s+/g, ' ');
    };

    return (
      <div
        ref={ref}
        role={selectionMode === 'single' ? 'radiogroup' : 'group'}
        className={trackClasses}
      >
        {items.map((item) => {
          const active = isSelected(item.value);

          return (
            <button
              key={item.value}
              type="button"
              role={selectionMode === 'single' ? 'radio' : 'checkbox'}
              aria-checked={active}
              aria-label={item.ariaLabel ?? item.label}
              disabled={disabled || item.disabled}
              onClick={() => handleClick(item.value)}
              className={getItemClasses(item)}
            >
              {item.icon && (
                <span className="inline-flex items-center justify-center flex-shrink-0 leading-none">
                  {item.icon}
                </span>
              )}
              {item.label && (
                <span className="inline-flex items-center leading-none">{item.label}</span>
              )}
            </button>
          );
        })}
      </div>
    );
  }
);

SegmentedControl.displayName = 'SegmentedControl';


